import React, { useMemo } from "react";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import { useSelector } from "../../store";
import MobileDataGrid from "../base/MobileDataGrid";
import TableActions from "../base/TableActions";
import { buildUserRowMetadata } from "../../helpers/userRowUtils";

interface Props {
  handleEdit?: (id: number) => void;
  handleDelete?: (id: number) => void;
  handleInvite?: (id: number) => void;
  handleAppRoute?: (id: number, url: string) => void;
}

const UsersStack: React.FC<Props> = (props) => {
  const { handleEdit, handleDelete, handleInvite, handleAppRoute } = props;

  const { userList } = useSelector((store) => store.user);
  const { dbUser } = useSelector((store) => store.auth);
  const { enabledApplications } = useSelector((store) => store.application);
  const { licenses } = useSelector((store) => store.company);

  const users = useMemo(
    () =>
      buildUserRowMetadata(
        userList || [],
        enabledApplications,
        dbUser,
        handleAppRoute,
        licenses,
      ),
    [userList, enabledApplications, dbUser, handleAppRoute, licenses],
  );

  if (!users.length) return null;

  return (
    <MobileDataGrid
      rows={users}
      getRowId={(row) => row.user.id}
      renderRow={({ user, canEdit, appRoutes }) => (
        <Stack spacing={1}>
          <Box display="flex" flexDirection="column">
            <Typography fontWeight="bold">
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {user.roles?.map((role) => (
              <Chip
                key={role.id}
                label={role.value}
                size="small"
                color="info"
              />
            ))}
          </Stack>
          {canEdit && (
            <>
              <Divider />
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    User Actions
                  </Typography>
                  <TableActions
                    id={user.id!}
                    justifyContent="flex-start"
                    handleEdit={
                      handleEdit ? () => handleEdit(user.id!) : undefined
                    }
                    handleDelete={
                      handleDelete ? () => handleDelete(user.id!) : undefined
                    }
                    handleInvite={
                      handleInvite ? () => handleInvite(user.id!) : undefined
                    }
                  />
                </Box>
                {appRoutes.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Edit in App
                    </Typography>
                    <TableActions
                      id={user.id!}
                      handleAppRoutes={appRoutes}
                      justifyContent="flex-start"
                    />
                  </Box>
                )}
              </Stack>
            </>
          )}
        </Stack>
      )}
    />
  );
};

export default UsersStack;
